import cn from 'classnames';
import { forwardRef, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { EXIT_FALLBACK_BUFFER_MS, getMaxTransitionTimeMs, useLatestRef } from './modal-utils';

import type { HTMLAttributes, ReactNode, TransitionEvent } from 'react';

import type { AnimationState } from './modal-utils';
import type { ModalWidth } from './modal.types';

/** Props accepted by {@link ModalTransition}. */
export interface ModalTransitionProps extends HTMLAttributes<HTMLDivElement> {
  /** Content to render inside the animated wrapper. */
  children: ReactNode;
  /** Whether the modal is in the open (entered) state. */
  in: boolean;
  /** Called once when the enter transition starts. */
  onEnter?: () => void;
  /** Called once when the exit transition has fully completed. */
  onExited?: () => void;
  /** Called each time the animation phase changes. */
  onStateChange?: (state: AnimationState) => void;
  /** Width variant forwarded as a BEM modifier class on the wrapper element. */
  width: ModalWidth;
}

/**
 * Internal wrapper that drives the CSS enter/exit animation for the modal.
 *
 * Applies `data-entering` / `data-entered` / `data-exiting` / `data-exited`
 * attributes and matching BEM modifier classes so that SCSS can target each
 * animation phase independently.
 *
 * The exit transition is also guarded by a fallback `setTimeout` so that
 * `onExited` fires reliably even when the CSS `transitionend` event is
 * suppressed — for example under `prefers-reduced-motion` or in jsdom tests.
 *
 * @internal
 */
const ModalTransition = forwardRef<HTMLDivElement, ModalTransitionProps>((props, forwardedRef) => {
  const { children, className, in: isOpen, onEnter, onExited, onStateChange, width, ...rest } = props;
  const [animationState, dispatchAnimationState] = useReducer(
    (_previousState: AnimationState, nextState: AnimationState) => nextState,
    'exited',
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const prevOpenRef = useRef(false);
  const enterAnimationFrameRef = useRef<number>(0);
  const settleAnimationFrameRef = useRef<number>(0);
  const exitTimeoutRef = useRef<number>(0);
  const onEnterRef = useLatestRef(onEnter);
  const onExitedRef = useLatestRef(onExited);
  const onStateChangeRef = useLatestRef(onStateChange);

  const mergedRef = useMemo(() => mergeRefs([wrapperRef, forwardedRef]), [forwardedRef]);

  /** Cancels any pending `requestAnimationFrame` calls for the enter sequence. */
  const clearAnimationFrameRefs = useCallback((): void => {
    if (enterAnimationFrameRef.current) {
      cancelAnimationFrame(enterAnimationFrameRef.current);
      enterAnimationFrameRef.current = 0;
    }

    if (settleAnimationFrameRef.current) {
      cancelAnimationFrame(settleAnimationFrameRef.current);
      settleAnimationFrameRef.current = 0;
    }
  }, []);

  /** Clears the fallback exit timeout. */
  const clearExitTimeout = useCallback((): void => {
    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = 0;
    }
  }, []);

  /**
   * Dispatches `state` to the local reducer and notifies the `onStateChange`
   * callback with the latest reference so that callers never see a stale value.
   */
  const syncAnimationState = useCallback(
    (state: AnimationState): void => {
      dispatchAnimationState(state);
      onStateChangeRef.current?.(state);
    },
    [onStateChangeRef],
  );

  /**
   * Completes the exit transition: clears the fallback timeout, sets the state
   * to `"exited"`, and fires the `onExited` callback.
   */
  const finishExitTransition = useCallback((): void => {
    clearExitTimeout();
    syncAnimationState('exited');
    onExitedRef.current?.();
  }, [clearExitTimeout, onExitedRef, syncAnimationState]);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = isOpen;

    clearAnimationFrameRefs();
    clearExitTimeout();

    if (isOpen) {
      syncAnimationState('entering');
      onEnterRef.current?.();

      // Two nested rAFs ensure the browser has committed the `entering` class
      // before switching to `entered`, which is required for CSS transitions to
      // fire correctly.
      enterAnimationFrameRef.current = requestAnimationFrame(() => {
        settleAnimationFrameRef.current = requestAnimationFrame(() => {
          syncAnimationState('entered');
        });
      });

      return () => {
        clearAnimationFrameRefs();
        clearExitTimeout();
      };
    }

    if (!wasOpen) {
      syncAnimationState('exited');
      return () => {
        clearAnimationFrameRefs();
        clearExitTimeout();
      };
    }

    syncAnimationState('exiting');

    const exitDurationMs = getMaxTransitionTimeMs(wrapperRef.current);

    if (exitDurationMs === 0) {
      finishExitTransition();
      return () => {
        clearAnimationFrameRefs();
        clearExitTimeout();
      };
    }

    const exitTimeoutId = window.setTimeout(() => {
      finishExitTransition();
    }, exitDurationMs + EXIT_FALLBACK_BUFFER_MS);

    exitTimeoutRef.current = exitTimeoutId;

    return () => {
      clearAnimationFrameRefs();
      clearTimeout(exitTimeoutId);

      if (exitTimeoutRef.current === exitTimeoutId) {
        exitTimeoutRef.current = 0;
      }
    };
  }, [clearAnimationFrameRefs, clearExitTimeout, finishExitTransition, isOpen, onEnterRef, syncAnimationState]);

  const handleTransitionEnd = (event: TransitionEvent<HTMLDivElement>): void => {
    if (event.target !== wrapperRef.current || isOpen || animationState !== 'exiting') {
      return;
    }

    finishExitTransition();
  };

  const wrapperClassName = cn('rbui-modal__wrapper', className, `rbui-modal__wrapper--${width}`, {
    'rbui-modal__wrapper--entered': animationState === 'entered',
    'rbui-modal__wrapper--entering': animationState === 'entering',
    'rbui-modal__wrapper--exited': animationState === 'exited',
    'rbui-modal__wrapper--exiting': animationState === 'exiting',
  });

  return (
    <div {...rest} className={wrapperClassName} onTransitionEnd={handleTransitionEnd} ref={mergedRef}>
      {children}
    </div>
  );
});

ModalTransition.displayName = 'ModalTransition';

export default ModalTransition;
